/**
 * Felix's approval card (row fad9bff7).
 *
 * Founder direction 6234968880 item 2: Felix never approves or advances
 * himself. When an entity is ready, or a phase is finished, he proposes; this
 * card shows the person exactly what they would approve (the entity's fields
 * as they were when Felix proposed, or the phase it moves to) and only their
 * Approve runs it, as them (POST /felix/proposals/{id}/approve). Change closes
 * the card and puts a prompt in the chat input so they say what to change.
 *
 * A field still holding the words Felix drafted says "Felix drafted this".
 */
import { useState } from "react";
import { Check, CheckCircle2, AlertCircle, Loader2, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { felixProposalsApi } from "@/api/services";
import type { FelixProposal } from "@/api/types";

export interface FelixProposalCardProps {
  proposal: FelixProposal;
  /** Change was pressed: the prompt to put in the chat input. */
  onChange?: (prompt: string) => void;
  /** A decision was recorded: the card as it is now, and what to tell the person. */
  onDecided?: (proposal: FelixProposal, message: string) => void;
}

function entityWord(entityType: string): string {
  return entityType.replace(/_/g, " ");
}

function failureText(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  return "That didn't go through. Please try again.";
}

function decidedLine(p: FelixProposal, message: string | null): string {
  if (message) return message;
  if (p.status === "approved") return "Approved.";
  if (p.status === "changed") return "You asked for changes.";
  if (p.status === "expired") return "This changed after Felix showed it to you, so it was not approved.";
  return "";
}

export function FelixProposalCard({ proposal, onChange, onDecided }: FelixProposalCardProps) {
  const [current, setCurrent] = useState<FelixProposal>(proposal);
  const [busy, setBusy] = useState<"approve" | "change" | null>(null);
  const [message, setMessage] = useState<string | null>(current.outcome?.message ?? null);
  const [error, setError] = useState<string | null>(null);
  const pending = current.status === "pending";
  const isPhase = current.kind === "phase_advance";

  async function handleApprove() {
    setBusy("approve");
    setError(null);
    try {
      const res = await felixProposalsApi.approve(current.id);
      setCurrent(res.proposal);
      setMessage(res.message);
      onDecided?.(res.proposal, res.message);
    } catch (err) {
      setError(failureText(err));
    } finally {
      setBusy(null);
    }
  }

  async function handleChange() {
    setBusy("change");
    setError(null);
    try {
      const res = await felixProposalsApi.change(current.id);
      setCurrent(res.proposal);
      setMessage(null);
      onChange?.(res.prompt);
      onDecided?.(res.proposal, "");
    } catch (err) {
      setError(failureText(err));
    } finally {
      setBusy(null);
    }
  }

  const heading = isPhase
    ? `Move to ${current.to_label || "the next phase"}?`
    : current.items.length === 1
      ? `Approve this ${entityWord(current.items[0].entity_type)}?`
      : `Approve these ${current.items.length} items?`;

  return (
    <div
      data-testid="felix-proposal-card"
      data-proposal-status={current.status}
      className="rounded-md border border-brand-200 bg-white px-3 py-2 text-xs dark:border-brand-700/50 dark:bg-slate-900"
    >
      <p className="mb-1 font-medium text-slate-800 dark:text-slate-100">{heading}</p>
      {current.summary && (
        <p className="mb-2 whitespace-pre-wrap text-slate-600 dark:text-slate-300">{current.summary}</p>
      )}

      {isPhase ? (
        <p data-testid="felix-proposal-phase" className="mb-2 text-slate-700 dark:text-slate-200">
          {current.from_label || "This phase"} → <span className="font-medium">{current.to_label}</span>
        </p>
      ) : (
        <ul className="mb-2 space-y-2">
          {current.items.map((item) => (
            <li key={item.entity_id} data-testid="felix-proposal-item">
              <p className="font-medium text-slate-800 dark:text-slate-100">
                {item.title || "Untitled"}{" "}
                <span className="font-normal text-slate-500 dark:text-slate-400">
                  ({entityWord(item.entity_type)})
                </span>
              </p>
              {(item.risky || item.unclarified) && (
                <p className="mt-0.5 flex flex-wrap gap-1">
                  {item.risky && (
                    <span
                      data-testid="felix-proposal-risky"
                      className="rounded bg-red-50 px-1 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                    >
                      Open conflict
                    </span>
                  )}
                  {item.unclarified && (
                    <span
                      data-testid="felix-proposal-unclarified"
                      className="rounded bg-amber-50 px-1 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                    >
                      Open questions
                    </span>
                  )}
                </p>
              )}
              {item.fields.length > 0 && (
                <dl className="mt-1 space-y-1">
                  {item.fields.map((field) => (
                    <div key={field.label}>
                      <dt className="text-slate-500 dark:text-slate-400">
                        {field.label}
                        {field.drafted && (
                          <span
                            data-testid="felix-proposal-drafted"
                            className="ml-1.5 rounded bg-amber-50 px-1 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                          >
                            Felix drafted this
                          </span>
                        )}
                      </dt>
                      <dd className="whitespace-pre-wrap text-slate-700 dark:text-slate-200">
                        {field.value || "—"}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </li>
          ))}
        </ul>
      )}

      {pending && message && (
        <p data-testid="felix-proposal-message" className="mb-2 flex items-start gap-1 text-amber-700 dark:text-amber-300">
          <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
          {message}
        </p>
      )}

      {pending ? (
        <div className="flex gap-2">
          <Button size="sm" className="h-7 text-xs" onClick={handleApprove} disabled={busy !== null}>
            {busy === "approve" ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="mr-1 h-3.5 w-3.5" />
            )}
            Approve
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={handleChange}
            disabled={busy !== null}
          >
            <Pencil className="mr-1 h-3.5 w-3.5" />
            Change
          </Button>
        </div>
      ) : (
        <p
          data-testid="felix-proposal-status"
          className={
            current.status === "approved"
              ? "flex items-center gap-1 text-green-700 dark:text-green-400"
              : "flex items-center gap-1 text-slate-600 dark:text-slate-300"
          }
        >
          {current.status === "approved" ? (
            <CheckCircle2 className="h-3.5 w-3.5" />
          ) : (
            <AlertCircle className="h-3.5 w-3.5" />
          )}
          {decidedLine(current, message)}
        </p>
      )}

      {error && (
        <p role="alert" className="mt-1 text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  );
}

export default FelixProposalCard;
